/**
 * Mappers for the Documents-tab server-side data path (DV-330 / DV-248):
 *
 *   1. `apiVaultDocumentToVaultDocument` — a server `VaultDocument` node ->
 *      the presentational `VaultDocument` (`#common/types/vault`) that
 *      `DocumentBrowser` renders.
 *   2. `gridModelToVaultDocumentsInput` — an AG Grid filter/sort/pagination
 *      model -> the `VaultDocumentsInput` the query accepts.
 *
 * Several things the client-side AG Grid can express are degraded here. Each is
 * marked `NOT SUPPORTED` (no server equivalent at all) or `NOT TRANSLATED YET`
 * (the input can express it; this translator doesn't), so both are greppable.
 * Verified against the merged supergraph SDL's `VaultDocumentAttributeFilter`
 * (`attributeId`, required `operator`, `text` / `textValues` / `number` /
 * `numberTo` / `date` / `dateTo`) as of 2026-08-05.
 *
 * NOT SUPPORTED by `VaultDocumentsInput` (dropped in the translator, with TODOs):
 *   - Free-text / filename search (there is no filename/full-text filter field —
 *     only `orderBy`).
 *   - Sorting on arbitrary attribute columns (`orderBy` is a fixed enum:
 *     created_at, filename, space_name, domain_slug, updated_at).
 *
 * NOT TRANSLATED YET (expressible on the input, dropped or narrowed here):
 *   - Combined conditions (`{ operator: "AND", conditions: [...] }`). One
 *     `VaultDocumentAttributeFilter` carries one predicate, so the controls are
 *     pinned to `maxNumConditions: 1` and a combined model is dropped.
 *   - Filtering on the SHARING status (Internal/External) — no backend field.
 *     The filing lifecycle IS filterable, via `validationStatus`, but only over
 *     `validated` / `quarantined`: there is no `pending` member, so a
 *     `pending` document is only reachable by omitting the filter entirely.
 *
 * PARTIAL / lossy on read:
 *   - `VaultDocument` has no `domain` field; the presentational `domain` tag is
 *     derived from the owning space via `options.domainBySpaceId`.
 *   - The backend `status` field is the FILING LIFECYCLE (pending / validated /
 *     quarantined), not Internal/External sharing. It is carried through as
 *     `lifecycle` (see `toDocumentLifecycle`). The presentational `status`
 *     (sharing) has no backend field yet and stays "Internal".
 *   - Version history isn't fetched by search; `versions` carries at most the
 *     `currentVersion` as a single entry. The document-detail route fetches full
 *     history via `vaultDocumentByRef` instead (see `useVaultDocumentByRef`), and
 *     passes its node's `versions` array straight through here.
 *   - Space-local attribute values are only keyed by name when a
 *     `localNameByAttributeId` map is supplied (single-space scope); otherwise
 *     only global-attribute values populate `attrs`.
 */
import { AgGrid } from "@jsq/ds/core";
import { toBareReferenceId } from "#common/globalId";
import {
  type VaultAttributeFilterOperator,
  type VaultDocumentOrderBy,
  type VaultDocumentsInput,
  type VaultDocumentValidationStatus,
} from "#graphql/supergraphTypes";
import { isTruthyAttributeValue } from "./uploadMappers";
import type {
  DocumentLifecycle,
  DocumentStatus,
  DocumentVersion,
  GlobalAttrValues,
  SpaceLocalValues,
  VaultDocument,
} from "#common/types/vault";
import type { VaultDocumentDetailNode } from "./hooks/useVaultDocumentByRef";
import type { VaultDocumentNode } from "./hooks/useVaultDocumentsSearch";

/* -------------------------------------------------------------------------- */
/* Read: server VaultDocument -> presentational VaultDocument                 */
/* -------------------------------------------------------------------------- */

/** Split a filename into its base name and lower-cased extension. */
const splitFilename = (filename: string): { base: string; ext: string } => {
  const dot = filename.lastIndexOf(".");
  if (dot <= 0) return { base: filename, ext: "doc" };
  return { base: filename.slice(0, dot), ext: filename.slice(dot + 1).toLowerCase() };
};

/**
 * Format an ISO timestamp as the short display date the presentational model
 * expects ("Feb 1, 2026"). Formatted in UTC so it's deterministic regardless of
 * the runtime timezone; falls back to the raw string if it doesn't parse.
 */
const DISPLAY_DATE_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  year: "numeric",
  month: "short",
  day: "numeric",
});
export const formatDisplayDate = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : DISPLAY_DATE_FMT.format(parsed);
};

/**
 * Coerce the backend's free `status` string to the presentational union. The
 * backend enum isn't finalized, so this is intentionally forgiving: anything
 * that reads "external" is External, everything else is Internal.
 */
export const toDocumentStatus = (status: string): DocumentStatus =>
  /external/i.test(status) ? "External" : "Internal";

/**
 * Coerce the backend's `VaultDocumentStatus` (`pending` / `validated` /
 * `quarantined`) into the presentational lifecycle. This is a DIFFERENT axis
 * from {@link toDocumentStatus}: that one reports Internal/External sharing.
 * Passing the lifecycle value through `toDocumentStatus` collapses all three to
 * "Internal", which is why the filing status has to be carried separately.
 */
export const toDocumentLifecycle = (status: string): DocumentLifecycle | undefined => {
  const value = status.trim().toLowerCase();
  return value === "pending" || value === "validated" || value === "quarantined"
    ? value
    : undefined;
};

/**
 * Render one attribute value as the display string the presentational model
 * uses (`attrs`/`local` are `Record<string, string>`). `valueNumber` arrives as
 * a string (Decimal scalar); booleans render as Yes/No.
 *
 * For reference attributes, names are resolved directly from the federated
 * `valueArenaList` (Client), `valueEntityList` (Entity), or `valueLpUserList`
 * (LP / Position — DV-572) fields — at most one is non-null per row. Arena and
 * Entity nodes expose `name` directly; Position labels come from nested
 * `account { name }`. Select attributes have no federated name list: DVB
 * returns `valueIds` (option GlobalIDs) and the matching labels on
 * `attribute.options` (already batched onto this field). Fall back to
 * `optionValueById` (the inverted GlobalAttributes catalog) when the nested
 * options were not selected. Unknown ids stay as the raw string. Genuinely
 * null values (no list, no ids) return `""` and display as "—" via the
 * grid's `dashFormatter`.
 */
export const attributeValueToString = (
  v: VaultDocumentNode["attributeValues"][number],
  optionValueById: Record<string, string> = {},
): string => {
  if (v.valueText != null) return v.valueText;
  if (v.valueNumber != null) return String(v.valueNumber);
  if (v.valueDate != null) return v.valueDate;
  if (v.valueBoolean != null) return v.valueBoolean ? "Yes" : "No";
  // Reference: prefer federated name lists; fall back to catalog / raw ids.
  if (v.valueArenaList != null && v.valueArenaList.length > 0) {
    return v.valueArenaList.map((node) => node.name || node.id).join(", ");
  }
  if (v.valueEntityList != null && v.valueEntityList.length > 0) {
    return v.valueEntityList.map((node) => node.name || node.id).join(", ");
  }
  if (v.valueLpUserList != null && v.valueLpUserList.length > 0) {
    return v.valueLpUserList
      .map((node) => node.account?.name || node.account?.id || node.id)
      .join(", ");
  }
  if (v.valueIds != null && v.valueIds.length > 0) {
    const fromAttribute = Object.fromEntries(
      (v.attribute.options ?? []).map((option) => [option.id, option.value]),
    );
    return v.valueIds.map((id) => fromAttribute[id] ?? optionValueById[id] ?? id).join(", ");
  }
  return "";
};

export interface ApiVaultDocumentToVaultDocumentOptions {
  /** spaceId -> presentational vault domain id, to populate the derived `domain` tag. */
  domainBySpaceId?: Record<string, string>;
  /** backend attributeId -> presentational global attribute `key` (drives `attrs`). */
  globalKeyByAttributeId?: Record<string, string>;
  /** backend attributeId -> space-local attribute name (single-space scope; drives `local`). */
  localNameByAttributeId?: Record<string, string>;
  /**
   * AttributeValueOption id -> display value. Fallback when the document node
   * did not carry `attribute.options` (the fragment now selects them). Invert
   * the catalog's `optionIdsByValue` via `optionValueByIdFromCatalog`.
   */
  optionValueById?: Record<string, string>;
}

/**
 * Map a server document node onto the presentational `VaultDocument`. Global
 * and space-local attribute values are keyed via the supplied lookups (an
 * attribute with no lookup entry is skipped). See the module header for the
 * fields that are derived/defaulted because search doesn't return them.
 *
 * `node.versions` is optional because `vaultDocumentsSearch` nodes don't carry
 * it — only a `vaultDocumentByRef` node does (see {@link VaultDocumentDetailNode}).
 */
export const apiVaultDocumentToVaultDocument = (
  node: VaultDocumentNode & Partial<Pick<VaultDocumentDetailNode, "versions">>,
  options: ApiVaultDocumentToVaultDocumentOptions = {},
): VaultDocument => {
  const {
    domainBySpaceId = {},
    globalKeyByAttributeId = {},
    localNameByAttributeId = {},
    optionValueById = {},
  } = options;

  const filename = node.currentVersion?.filename ?? "";
  const { base, ext } = filename
    ? splitFilename(filename)
    : { base: node.publicRefId, ext: "doc" };

  const attrs: GlobalAttrValues = {};
  const local: SpaceLocalValues = {};
  // Picked ids for id-shaped attributes (DV-466), keyed like `attrs`/`local`.
  const attrIds: Record<string, string[]> = {};
  // Tri-state per attribute (DV-503): every entry the backend returns is bound
  // to the document's space — `isSet` says whether it carries a value. A key
  // ABSENT from this map (while the map itself is present) means the attribute
  // is not bound to the space at all ("not applicable").
  const attrStates: Record<string, "set" | "empty"> = {};
  node.attributeValues.forEach((v) => {
    const attributeId = v.attribute.id;
    const ids = v.valueIds ?? undefined;
    const state = v.isSet ? "set" : "empty";
    const globalKey = globalKeyByAttributeId[attributeId];
    if (globalKey) {
      attrs[globalKey] = attributeValueToString(v, optionValueById);
      attrStates[globalKey] = state;
      if (ids && ids.length > 0) attrIds[globalKey] = ids;
      return;
    }
    const localName = localNameByAttributeId[attributeId];
    if (localName) {
      local[localName] = attributeValueToString(v, optionValueById);
      attrStates[localName] = state;
      if (ids && ids.length > 0) attrIds[localName] = ids;
    }
    // Attributes with neither lookup entry are dropped (see header).
  });

  // `node.versions` (from `vaultDocumentByRef`) is the full history. The backend
  // orders it `ssb_version_number DESC`, but sort defensively rather than trust
  // that ordering — `DocumentDetail` treats index 0 as "Current".  A search node
  // has no `versions` field at all, so fall back to surfacing `currentVersion`
  // as a single entry.
  const versions: DocumentVersion[] = node.versions?.length
    ? [...node.versions]
        .sort((a, b) => b.ssbVersionNumber - a.ssbVersionNumber)
        .map((v) => ({
          n: `v${v.ssbVersionNumber}`,
          note: v.changeNote ?? "",
          // `uploadedBy` is a raw backend user id and `VaultDocumentVersion`
          // carries no name to resolve it with, so the timeline read "uploaded by
          // 3753" (DV-601). Show the uploader's role instead until DV-550 wires up
          // real name resolution — the same default the `currentVersion` path
          // below already uses.
          who: "Data Vault Admin",
          date: formatDisplayDate(v.createdAt),
          fileName: v.filename,
        }))
    : node.currentVersion
      ? [
          {
            n: `v${node.currentVersion.ssbVersionNumber}`,
            note: "",
            who: "",
            // Uploader role is not returned by the search fragment (no
            // per-version uploader field on VaultDocument.currentVersion), so
            // default to "Data Vault Admin" until DV-550 wires up real resolution.
            role: "Data Vault Admin",
            date: formatDisplayDate(node.updatedAt),
            fileName: node.currentVersion.filename,
          },
        ]
      : [];

  return {
    // `VaultDocument` has no `id`; `publicRefId` is its stable identifier.
    id: node.publicRefId,
    file: ext,
    name: base,
    space: node.spaceId,
    // NOT a backend field on VaultDocument — derived from the owning space.
    domain: domainBySpaceId[node.spaceId] ?? "",
    // Sharing status. NOTE: the backend `status` field is the filing lifecycle,
    // not sharing — so this stays "Internal" until sharing is backed. The real
    // backend value is carried on `lifecycle` below.
    status: toDocumentStatus(node.status),
    lifecycle: toDocumentLifecycle(node.status),
    // `modified` is a display string ("Feb 1, 2026") — `DocumentBrowser` renders
    // it verbatim, so format the ISO `updatedAt` here rather than downstream.
    modified: formatDisplayDate(node.updatedAt),
    attrs,
    local,
    // Absent rather than an empty object ON PURPOSE: `attrIds` is the "did this
    // read carry any picked ids?" signal, and the edit form branches on it to
    // decide whether an untouched row still has ids worth resending. An
    // always-present `{}` would answer that question with a lie.
    ...(Object.keys(attrIds).length > 0 ? { attrIds } : {}),
    // Unlike `attrIds`, ALWAYS present (even `{}`) on backend-read documents:
    // the map's presence is itself the "tri-state info exists" signal that lets
    // the grid render "N/A" for unbound attributes. Presentational mocks never
    // pass through this mapper and keep rendering every blank as "—".
    attrStates,
    versions,
  };
};

/* -------------------------------------------------------------------------- */
/* Write: AG Grid model -> VaultDocumentsInput                                */
/* -------------------------------------------------------------------------- */

/** Backend attribute value-type -> which equality field of the filter to set. */
export type AttributeValueTypeKind =
  "text" | "number" | "date" | "boolean" | "single_select" | "multi_select" | "reference";

/** Metadata for an AG Grid column that maps to a backend attribute predicate. */
export interface AttributeColumnMeta {
  attributeId: string;
  valueType: AttributeValueTypeKind;
  /**
   * A `space_local` attribute. document-vault-backend's `_check_attribute_kind_scope`
   * rejects a space-local filter unless the query names EXACTLY ONE space, so one
   * of these outside a single-space scope is dropped here rather than failing the
   * whole block fetch.
   */
  spaceLocal?: boolean;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/* ---------------------------------------------------------------------------
 * The backend's operator contract.
 *
 * Mirrors document-vault-backend `schemas/attribute_filter.py`
 * (`ALLOWED_OPERATORS_BY_VALUE_TYPE`) and `services/document_service.py`
 * (`_CARRIERS_BY_OPERATOR`). Both are enforced: an operator the attribute's
 * value type doesn't allow, or a carrier the operator doesn't accept, raises
 * `DocumentValidationError` server-side. That surfaces as a GraphQL error, which
 * the datasource can only turn into `params.fail()` — the grid block dies rather
 * than degrading to an unfiltered result. So anything not expressible is dropped
 * HERE, deliberately, and the controls in `attributeFilters.ts` are built to
 * offer only what survives this table.
 *
 * The single most important row: reference / single_select / multi_select store
 * their value in `value_id` and accept ONLY `any_of` / `blank` / `not_blank`.
 * `equals` on any of them is rejected — which is what every Client, Entity and
 * select-attribute filter used to send.
 * ------------------------------------------------------------------------- */

type Operator = VaultAttributeFilterOperator;

const TEXT_OPERATORS = [
  "equals",
  "not_equal",
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
  "blank",
  "not_blank",
  "any_of",
] as const;

/** reference / single_select / multi_select — id-backed, membership only. */
const ID_SHAPED_OPERATORS = ["any_of", "blank", "not_blank"] as const;

const NUMBER_OPERATORS = [
  "equals",
  "not_equal",
  "greater_than",
  "greater_than_or_equal",
  "less_than",
  "less_than_or_equal",
  "in_range",
  "blank",
  "not_blank",
] as const;

const DATE_OPERATORS = [
  "equals",
  "in_range",
  "less_than",
  "greater_than",
  "greater_than_or_equal",
  "less_than_or_equal",
  "blank",
  "not_blank",
] as const;

const ALLOWED_OPERATORS: Record<AttributeValueTypeKind, ReadonlySet<Operator>> = {
  text: new Set<Operator>(TEXT_OPERATORS),
  number: new Set<Operator>(NUMBER_OPERATORS),
  date: new Set<Operator>(DATE_OPERATORS),
  // The one type restricted to plain equality.
  boolean: new Set<Operator>(["equals"]),
  single_select: new Set<Operator>(ID_SHAPED_OPERATORS),
  multi_select: new Set<Operator>(ID_SHAPED_OPERATORS),
  reference: new Set<Operator>(ID_SHAPED_OPERATORS),
};

/** AG Grid's filter `type` -> the backend operator name. */
const OPERATOR_BY_AG_TYPE: Record<string, Operator> = {
  equals: "equals",
  notEqual: "not_equal",
  contains: "contains",
  notContains: "not_contains",
  startsWith: "starts_with",
  endsWith: "ends_with",
  greaterThan: "greater_than",
  greaterThanOrEqual: "greater_than_or_equal",
  lessThan: "less_than",
  lessThanOrEqual: "less_than_or_equal",
  inRange: "in_range",
  blank: "blank",
  notBlank: "not_blank",
};

/** Operators that carry no value at all (`_CARRIERS_BY_OPERATOR` -> empty set). */
const VALUELESS_OPERATORS: ReadonlySet<Operator> = new Set<Operator>(["blank", "not_blank"]);

/**
 * document-vault-backend's `MIN_SUBSTRING_LENGTH` (D10). A shorter needle is a
 * validation error, not an empty result, so a half-typed `contains` is dropped
 * rather than sent — the user sees unfiltered rows for a keystroke instead of a
 * broken grid.
 */
const MIN_SUBSTRING_LENGTH = 3;

const SUBSTRING_OPERATORS: ReadonlySet<Operator> = new Set<Operator>([
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
]);

/**
 * The model our Client / Entity reference filters emit (see
 * `DocumentBrowser/referenceFilters.tsx`). Not an AG Grid built-in: a reference
 * value is an opaque id with a name that lives in another subgraph, so no stock
 * control can produce a usable predicate for one.
 */
export const REFERENCE_FILTER_TYPE = "vaultReference";

export type VaultReferenceFilterModel = {
  filterType: typeof REFERENCE_FILTER_TYPE;
  /** Bare pks — the form `document_attribute_values.value_id` stores. */
  ids: string[];
  /** The relay GlobalIDs the search components reported, kept for re-display. */
  globalIds?: string[];
  /** Display labels for the picked ids, kept so the chips survive a remount. */
  labels?: string[];
};

type AttributeFilter = NonNullable<VaultDocumentsInput["attributeFilters"]>[number];

/** Matches a leading calendar day in an AG Grid `dateFrom` / `dateTo` string. */
const LEADING_DAY = /^(\d{4}-\d{2}-\d{2})/;

/**
 * AG Grid serializes a picked date as `"2026-03-15 00:00:00"`, and the filter's
 * `date` is a raw `String` on the wire (no scalar validation), so a bad shape
 * doesn't error — it silently matches nothing. Trim to the calendar day the
 * backend's `date.fromisoformat` accepts.
 */
const asIsoDay = (raw: unknown): string | null => {
  if (raw == null) return null;
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- raw is an opaque AG Grid filter value, defensively stringified for date parsing
  return LEADING_DAY.exec(String(raw))?.[1] ?? null;
};

const asFiniteNumber = (raw: unknown): number | null => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * `any_of` + `textValues`, the only predicate an id-backed attribute accepts.
 *
 * For a `reference` the values must be BARE pks: the search path passes them
 * straight through to `value_id IN (...)` without decoding (unlike the write
 * path, which decodes the GlobalIDs it is handed). For a select they must be the
 * option's raw `value`, which the backend resolves to an option id — a label
 * that matches no configured option is a validation error, not an empty result,
 * which is why the control is built from the catalog's own option list.
 */
const anyOfFilter = (
  meta: AttributeColumnMeta,
  rawValues: readonly string[],
): AttributeFilter | null => {
  if (!rawValues.length) return null;
  const textValues =
    meta.valueType === "reference" ? rawValues.map(toBareReferenceId) : [...rawValues];
  return { attributeId: meta.attributeId, operator: "any_of", textValues };
};

/** A set filter (`agSetColumnFilter`) -> the predicate its value type allows. */
const fromSetModel = (
  meta: AttributeColumnMeta,
  model: Record<string, unknown>,
): AttributeFilter | null => {
  const values = Array.isArray(model.values)
    ? model.values.filter((v): v is string => v != null).map(String)
    : [];
  if (!values.length) return null;

  // Yes/No is the one set-filtered type that can't take `any_of`. A closed
  // two-value list means "one ticked" is the only model worth sending — with
  // both ticked AG Grid emits no model at all.
  if (meta.valueType === "boolean") {
    return values.length === 1
      ? {
          attributeId: meta.attributeId,
          operator: "equals",
          boolean: isTruthyAttributeValue(values[0]),
        }
      : null;
  }
  if (!ALLOWED_OPERATORS[meta.valueType].has("any_of")) return null;
  return anyOfFilter(meta, values);
};

/** A text / number / date filter -> the predicate its value type allows. */
const fromOperatorModel = (
  meta: AttributeColumnMeta,
  model: Record<string, unknown>,
): AttributeFilter | null => {
  // One `VaultDocumentAttributeFilter` is one predicate; the controls pin
  // `maxNumConditions: 1`, so a combined model means someone widened them.
  if (Array.isArray(model.conditions)) return null;

  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- model.type is an opaque AG Grid filter-model field, defensively stringified
  const operator = OPERATOR_BY_AG_TYPE[String(model.type ?? "")];
  if (!operator || !ALLOWED_OPERATORS[meta.valueType].has(operator)) return null;

  // `blank` / `not_blank` are existence-only and must travel with NO carrier —
  // a superfluous one is rejected outright.
  if (VALUELESS_OPERATORS.has(operator)) {
    return { attributeId: meta.attributeId, operator };
  }

  switch (meta.valueType) {
    case "number": {
      const from = asFiniteNumber(model.filter);
      if (from == null) return null;
      if (operator === "in_range") {
        const to = asFiniteNumber(model.filterTo);
        return to == null
          ? null
          : { attributeId: meta.attributeId, operator, number: from, numberTo: to };
      }
      return { attributeId: meta.attributeId, operator, number: from };
    }
    case "date": {
      const from = asIsoDay(model.dateFrom);
      if (from == null) return null;
      if (operator === "in_range") {
        const to = asIsoDay(model.dateTo);
        return to == null
          ? null
          : { attributeId: meta.attributeId, operator, date: from, dateTo: to };
      }
      return { attributeId: meta.attributeId, operator, date: from };
    }
    case "text": {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string -- model.filter is an opaque AG Grid filter-model field, defensively stringified
      const value = model.filter == null ? "" : String(model.filter);
      if (!value) return null;
      if (SUBSTRING_OPERATORS.has(operator) && value.trim().length < MIN_SUBSTRING_LENGTH) {
        return null;
      }
      return { attributeId: meta.attributeId, operator, text: value };
    }
    // boolean is only reachable through a set filter; id-shaped types have no
    // operator model at all (`ALLOWED_OPERATORS` already rejected them above).
    default:
      return null;
  }
};

/**
 * Build the one `VaultDocumentAttributeFilter` an AG Grid column model maps to,
 * or `null` when the backend can't express it.
 */
const attributeFilterFor = (
  meta: AttributeColumnMeta,
  filter: unknown,
): AttributeFilter | null => {
  if (!isObject(filter)) return null;
  switch (filter.filterType) {
    case REFERENCE_FILTER_TYPE:
      return Array.isArray(filter.ids) ? anyOfFilter(meta, filter.ids.map(String)) : null;
    case "set":
      return fromSetModel(meta, filter);
    case "text":
    case "number":
    case "date":
      return fromOperatorModel(meta, filter);
    default:
      return null;
  }
};

export interface AttributeFilterTranslationOptions {
  /**
   * Whether the query names exactly one space. Space-local attribute filters are
   * only legal then (`_check_attribute_kind_scope`), so they are dropped
   * otherwise. Defaults to true: every caller that supplies space-local columns
   * builds them from a single resolved space.
   */
  singleSpace?: boolean;
}

/**
 * Translate an AG Grid filter model into `attributeFilters`. Only columns
 * present in `attributeColumns` are considered; built-in columns (name, space,
 * domain, status) are handled outside this function — the topbar space/domain
 * filters feed `spaceIds`/`domain`, and there is no server-side status/filename
 * filter (see the module header).
 */
export const attributeFiltersFromFilterModel = (
  filterModel: AgGrid.FilterModel | null | undefined,
  attributeColumns: Record<string, AttributeColumnMeta>,
  { singleSpace = true }: AttributeFilterTranslationOptions = {},
): NonNullable<VaultDocumentsInput["attributeFilters"]> => {
  const filters: NonNullable<VaultDocumentsInput["attributeFilters"]> = [];
  if (!filterModel) return filters;

  Object.entries(filterModel).forEach(([colId, filter]) => {
    const meta = attributeColumns[colId];
    if (!meta) return; // built-in / non-attribute column: not a server filter here
    if (meta.spaceLocal && !singleSpace) return;
    const attrFilter = attributeFilterFor(meta, filter);
    if (attrFilter) filters.push(attrFilter);
  });

  return filters;
};

/**
 * AG Grid column ids that map to the five sortable server dimensions.
 */
const CREATED_AT_COL_IDS = new Set(["createdAt", "created_at"]);
const FILENAME_COL_IDS = new Set(["name", "filename"]);
const SPACE_NAME_COL_IDS = new Set(["space", "space_name"]);
const DOMAIN_SLUG_COL_IDS = new Set(["domain", "domain_slug"]);
const UPDATED_AT_COL_IDS = new Set(["modified", "updated_at", "updatedAt"]);

/**
 * Translate an AG Grid sort model into a `VaultDocumentOrderBy`. Only the first
 * sort item is honored, and only when it targets created-at, filename, space
 * name, domain slug, or updated-at — the backend enum supports nothing else
 * (returns `undefined` otherwise, leaving the server default). NOT SUPPORTED:
 * multi-column sort, or sorting on attribute columns.
 */
export const orderByFromSortModel = (
  sortModel: AgGrid.SortModelItem[] | null | undefined,
): VaultDocumentOrderBy | undefined => {
  const first = sortModel?.[0];
  if (!first) return undefined;
  const asc = first.sort === "asc";
  if (CREATED_AT_COL_IDS.has(first.colId)) return asc ? "created_at_asc" : "created_at_desc";
  if (FILENAME_COL_IDS.has(first.colId)) return asc ? "filename_asc" : "filename_desc";
  if (SPACE_NAME_COL_IDS.has(first.colId)) return asc ? "space_name_asc" : "space_name_desc";
  if (DOMAIN_SLUG_COL_IDS.has(first.colId)) return asc ? "domain_slug_asc" : "domain_slug_desc";
  if (UPDATED_AT_COL_IDS.has(first.colId)) return asc ? "updated_at_asc" : "updated_at_desc";
  // Unsupported sort column (e.g. attribute columns) — leave server default.
  return undefined;
};

export interface GridModelToInputOptions {
  /** Topbar space filter -> `spaceIds` (null/empty = all spaces). */
  spaceIds?: string[] | null;
  /** Topbar domain filter -> `domain` slug strings (null/empty = all domains). */
  domain?: string[] | null;
  /**
   * Health-tab filter -> `validationStatus`. `["validated"]` for the Filed tab,
   * `["quarantined"]` for Documents awaiting filing. Null/empty omits the field,
   * which is how the All tab is expressed: the enum has no `pending` member, so
   * enumerating both statuses would silently drop `pending` documents.
   */
  validationStatus?: VaultDocumentValidationStatus[] | null;
  /** Page size for this block (`first`). */
  first?: number | null;
  /** Forward cursor for this block (`after`). */
  after?: string | null;
  /** AG Grid attribute-column id -> backend attribute descriptor. */
  attributeColumns?: Record<string, AttributeColumnMeta>;
}

/**
 * Assemble a `VaultDocumentsInput` from an AG Grid request: the topbar
 * space/domain filters, the health tab's `validationStatus`, the grid filter
 * model (see `attributeFiltersFromFilterModel` for what survives translation),
 * the sort model (created_at/filename/space_name/domain_slug/updated_at only), and
 * forward-cursor pagination. Empty/absent fields are omitted so the server
 * defaults apply.
 */
export const gridModelToVaultDocumentsInput = (
  filterModel: AgGrid.FilterModel | null | undefined,
  sortModel: AgGrid.SortModelItem[] | null | undefined,
  options: GridModelToInputOptions = {},
): VaultDocumentsInput => {
  const { spaceIds, domain, validationStatus, first, after, attributeColumns = {} } = options;

  const attributeFilters = attributeFiltersFromFilterModel(filterModel, attributeColumns, {
    // Space-local filters are legal only when the query names exactly one space,
    // which is the same condition under which the grid builds space-local columns.
    singleSpace: spaceIds?.length === 1,
  });
  const orderBy = orderByFromSortModel(sortModel);

  const input: VaultDocumentsInput = {};
  if (spaceIds?.length) input.spaceIds = spaceIds;
  if (domain?.length) input.domain = domain;
  if (validationStatus?.length) input.validationStatus = validationStatus;
  if (attributeFilters.length) input.attributeFilters = attributeFilters;
  if (orderBy) input.orderBy = orderBy;
  if (first != null) input.first = first;
  if (after != null) input.after = after;
  return input;
};
